import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { layoutForPath } from '../utils/layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../components/ui/command';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ComplianceReport from '../components/reports/ComplianceReport';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DollarSign, Clock, TrendingUp, TrendingDown, Users, BarChart3, ChevronsUpDown, Check, AlertTriangle, Building2, Package, Wrench, ShieldCheck } from 'lucide-react';
import { cn } from '../lib/utils';
import DateField from '../components/DateField';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Reports() {
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState('');
  const [costReport, setCostReport] = useState(null);
  const [comboOpen, setComboOpen] = useState(false);
  const [techMargins, setTechMargins] = useState([]);
  const [modelMargins, setModelMargins] = useState([]);
  const [customerMargins, setCustomerMargins] = useState([]);
  const [maintenanceReport, setMaintenanceReport] = useState(null);
  const [maintRange, setMaintRange] = useState({ start_date: '', end_date: '' });
  const inAdminDivision = useLocation().pathname.startsWith('/admin/');
  const [activeTab, setActiveTab] = useState(inAdminDivision ? 'compliance' : 'job');
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const Layout = layoutForPath(useLocation().pathname);
  const maintRangeRef = useRef(maintRange);
  useEffect(() => { maintRangeRef.current = maintRange; }, [maintRange]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['job', 'technician', 'model', 'customer', 'maintenance', 'compliance'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const fetchMaintenanceReport = useCallback(async (range) => {
    const r = range || maintRangeRef.current;
    try {
      const params = {};
      if (r.start_date) params.start_date = r.start_date;
      if (r.end_date) params.end_date = r.end_date;
      const res = await axios.get(`${API}/reports/equipment-maintenance`, { params });
      setMaintenanceReport(res.data);
    } catch (e) {
      console.error('Failed to load maintenance report', e);
    }
  }, []);

  const fetchCostReport = useCallback(async (jobId) => {
    try {
      const response = await axios.get(`${API}/reports/job-cost/${jobId}`);
      setCostReport(response.data);
    } catch (error) {
      toast.error('Failed to load cost report');
    }
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/jobs?include_archived=true`);
      setJobs(response.data);
      if (response.data.length > 0) {
        setSelectedJob(response.data[0].id);
        fetchCostReport(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load jobs');
    }
  }, [fetchCostReport]);

  const fetchMarginReports = useCallback(async () => {
    try {
      const [techRes, modelRes, custRes] = await Promise.all([
        axios.get(`${API}/reports/margin-by-technician`),
        axios.get(`${API}/reports/margin-by-model`),
        axios.get(`${API}/reports/margin-by-customer`)
      ]);
      setTechMargins(techRes.data);
      setModelMargins(modelRes.data);
      setCustomerMargins(custRes.data);
    } catch (e) {
      console.error('Failed to load margin reports', e);
    }
  }, []);

  const effectivePerms = user?.effective_permissions || [];
  // Cost/margin tabs stay in the Shop division — the Admin & Compliance view is
  // deliberately money-free (maintenance + compliance only).
  const canJob = !inAdminDivision && effectivePerms.includes('reports.job');
  const canTech = !inAdminDivision && effectivePerms.includes('reports.technician');
  const canModel = !inAdminDivision && effectivePerms.includes('reports.model');
  const canCustomer = !inAdminDivision && effectivePerms.includes('reports.customer');
  const canMaintenance = effectivePerms.includes('reports.maintenance');
  const canCompliance = effectivePerms.includes('reports.compliance');

  useEffect(() => {
    if (canJob) fetchJobs();
    if (canTech || canModel || canCustomer) fetchMarginReports();
    if (canMaintenance) fetchMaintenanceReport();
  }, [canJob, canTech, canModel, canCustomer, canMaintenance, fetchJobs, fetchMarginReports, fetchMaintenanceReport]);

  const handleJobChange = (jobId) => {
    setSelectedJob(jobId);
    setComboOpen(false);
    fetchCostReport(jobId);
  };

  const selectedJobData = jobs.find(j => j.id === selectedJob);
  const selectedLabel = selectedJobData ? `${selectedJobData.job_number} - ${selectedJobData.customer_name}` : 'Select a job...';
  const hasTechData = costReport && Object.keys(costReport.technician_breakdown).length > 0;
  const hasEquipData = costReport && Object.keys(costReport.equipment_breakdown).length > 0;
  const hasCharge = costReport && costReport.charge_amount > 0;
  const profitPositive = costReport && costReport.profit >= 0;

  const allowedTabs = [
    canJob && 'job', canTech && 'technician', canModel && 'model',
    canCustomer && 'customer', canMaintenance && 'maintenance', canCompliance && 'compliance',
  ].filter(Boolean);

  // If activeTab is one they don't have access to, silently fall back to the first allowed.
  const effectiveActiveTab = allowedTabs.includes(activeTab) ? activeTab : (allowedTabs[0] || 'job');

  return (
    <Layout>
      <div data-testid="reports-page" className="space-y-6">
        <div>
          <h3 className="text-2xl font-bold text-slate-900 mb-2">
            {inAdminDivision ? 'Compliance & Maintenance' : 'Reports & Analytics'}
          </h3>
          <p className="text-slate-600">
            {inAdminDivision
              ? 'Certification status, preventative maintenance and compliance exports'
              : 'Job costs, revenue, and margin analysis'}
          </p>
        </div>

        {allowedTabs.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-md p-12 text-center text-slate-500" data-testid="no-reports-access">
            You don&apos;t have access to any reports yet. Ask your admin to grant specific report permissions from Settings.
          </div>
        ) : (
        <Tabs value={effectiveActiveTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full mb-6 ${
            allowedTabs.length === 1 ? 'grid-cols-1'
            : allowedTabs.length === 2 ? 'grid-cols-2'
            : allowedTabs.length === 3 ? 'grid-cols-3'
            : allowedTabs.length === 4 ? 'grid-cols-4'
            : allowedTabs.length === 5 ? 'grid-cols-5'
            : 'grid-cols-6'
          }`}>
            {canJob && (
              <TabsTrigger value="job" className="flex items-center gap-2" data-testid="tab-job">
                <BarChart3 size={16} />Job Cost
              </TabsTrigger>
            )}
            {canTech && (
              <TabsTrigger value="technician" className="flex items-center gap-2" data-testid="tab-technician">
                <Users size={16} />By Technician
              </TabsTrigger>
            )}
            {canModel && (
              <TabsTrigger value="model" className="flex items-center gap-2" data-testid="tab-model">
                <Package size={16} />By Model
              </TabsTrigger>
            )}
            {canCustomer && (
              <TabsTrigger value="customer" className="flex items-center gap-2" data-testid="tab-customer">
                <Building2 size={16} />By Customer
              </TabsTrigger>
            )}
            {canMaintenance && (
              <TabsTrigger value="maintenance" className="flex items-center gap-2" data-testid="tab-maintenance">
                <Wrench size={16} />Maintenance
              </TabsTrigger>
            )}
            {canCompliance && (
              <TabsTrigger value="compliance" className="flex items-center gap-2" data-testid="tab-compliance">
                <ShieldCheck size={16} />Compliance
              </TabsTrigger>
            )}
          </TabsList>

          {/* Job Cost Report Tab */}
          <TabsContent value="job">
            {jobs.length > 0 ? (
              <>
                {/* Job Selector - Searchable Combobox */}
                <Card className="p-6 border border-slate-200 shadow-sm mb-6">
                  <label className="block mb-2 text-sm font-semibold text-slate-900">Select Job</label>
                  <p className="mb-2 text-xs text-slate-500">Search by job #, customer, or any equipment serial # / unit ID to drill straight into that unit&apos;s cost report.</p>
                  <Popover open={comboOpen} onOpenChange={setComboOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={comboOpen}
                        className="w-full justify-between font-normal text-left h-10"
                        data-testid="job-combobox-trigger"
                      >
                        {selectedLabel}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search job #, customer, serial #, unit ID, make/model..." data-testid="job-search-input" />
                        <CommandList>
                          <CommandEmpty>No jobs found.</CommandEmpty>
                          <CommandGroup>
                            {jobs.map((job) => {
                              const eqTerms = (job.equipment || [])
                                .map((eq) => [eq.make, eq.model, eq.serial_number, eq.unit_id].filter(Boolean).join(' '))
                                .join(' ');
                              const eqLine = (job.equipment || []).length
                                ? (job.equipment || [])
                                    .map((eq) => [eq.make, eq.model, eq.serial_number && `S/N ${eq.serial_number}`, eq.unit_id && `Unit ${eq.unit_id}`].filter(Boolean).join(' '))
                                    .join(' | ')
                                : [job.make, job.model, job.serial_number && `S/N ${job.serial_number}`, job.unit_id && `Unit ${job.unit_id}`].filter(Boolean).join(' ');
                              return (
                                <CommandItem
                                  key={job.id}
                                  value={`${job.job_number} ${job.customer_name} ${job.description || ''} ${job.make || ''} ${job.model || ''} ${job.serial_number || ''} ${job.unit_id || ''} ${eqTerms}`}
                                  onSelect={() => handleJobChange(job.id)}
                                  data-testid={`job-option-${job.id}`}
                                >
                                  <Check className={cn("mr-2 h-4 w-4 shrink-0", selectedJob === job.id ? "opacity-100" : "opacity-0")} />
                                  <div className="min-w-0">
                                    <div>
                                      <span className="font-medium">{job.job_number}</span>
                                      <span className="text-slate-500 ml-2">- {job.customer_name}</span>
                                    </div>
                                    {eqLine && <p className="truncate text-xs text-slate-500">{eqLine}</p>}
                                  </div>
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </Card>

                {costReport && (
                  <>
                    {/* Summary Cards Row 1: Cost & Hours */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                      <Card className="p-5 border border-slate-200 shadow-sm" data-testid="total-cost-card">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-9 h-9 bg-red-50 rounded-lg flex items-center justify-center">
                            <DollarSign size={18} className="text-red-600" />
                          </div>
                          <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Total Cost</p>
                        </div>
                        <p className="text-3xl font-black text-slate-900" data-testid="total-cost-value">${costReport.total_cost.toFixed(2)}</p>
                      </Card>
                      <Card className="p-5 border border-slate-200 shadow-sm" data-testid="total-hours-card">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center">
                            <Clock size={18} className="text-blue-600" />
                          </div>
                          <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Total Hours</p>
                        </div>
                        <p className="text-3xl font-black text-slate-900" data-testid="total-hours-value">{costReport.total_hours.toFixed(1)}</p>
                        {(costReport.total_overtime_hours || 0) > 0 && (
                          <p className="text-xs font-semibold text-amber-700 mt-1" data-testid="total-overtime-hours">
                            +{costReport.total_overtime_hours.toFixed(1)} OT hrs · ${(costReport.total_overtime_cost || 0).toFixed(2)}
                          </p>
                        )}
                      </Card>
                      <Card className="p-5 border border-slate-200 shadow-sm" data-testid="charge-amount-card">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center">
                            <DollarSign size={18} className="text-emerald-600" />
                          </div>
                          <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Revenue</p>
                        </div>
                        {hasCharge ? (
                          <p className="text-3xl font-black text-emerald-700" data-testid="charge-amount-value">${costReport.charge_amount.toFixed(2)}</p>
                        ) : (
                          <p className="text-sm text-slate-400 mt-1" data-testid="no-charge">Set on job page</p>
                        )}
                      </Card>
                      <Card className={`p-5 border shadow-sm ${hasCharge ? (profitPositive ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50') : 'border-slate-200'}`} data-testid="profit-card">
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${hasCharge ? (profitPositive ? 'bg-emerald-100' : 'bg-red-100') : 'bg-slate-100'}`}>
                            {hasCharge ? (
                              profitPositive ? <TrendingUp size={18} className="text-emerald-600" /> : <AlertTriangle size={18} className="text-red-600" />
                            ) : (
                              <TrendingUp size={18} className="text-slate-400" />
                            )}
                          </div>
                          <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Profit</p>
                        </div>
                        {hasCharge ? (
                          <div>
                            <p className={`text-3xl font-black ${profitPositive ? 'text-emerald-700' : 'text-red-600'}`} data-testid="profit-value">
                              {profitPositive ? '' : '-'}${Math.abs(costReport.profit).toFixed(2)}
                            </p>
                            <p className={`text-sm font-semibold mt-1 ${profitPositive ? 'text-emerald-600' : 'text-red-500'}`} data-testid="margin-value">
                              {costReport.margin.toFixed(1)}% margin
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm text-slate-400 mt-1">Set charge on job</p>
                        )}
                      </Card>
                    </div>

                    {/* Job Details */}
                    {selectedJobData && (
                      <Card className="p-6 border border-slate-200 shadow-sm mb-6" data-testid="job-details-card">
                        <h4 className="text-lg font-bold text-slate-900 mb-4">Job Details</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Job Number</p>
                            <p className="text-slate-900 font-mono">{selectedJobData.job_number}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Customer</p>
                            <p className="text-slate-900">{selectedJobData.customer_name}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Status</p>
                            <p className="text-slate-900 capitalize">{selectedJobData.status?.replace('_', ' ')}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Timeline</p>
                            <p className="text-slate-900">
                              {new Date(selectedJobData.start_date).toLocaleDateString()} - {new Date(selectedJobData.end_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </Card>
                    )}

                    {/* Technician Breakdown */}
                    <Card className="p-6 border border-slate-200 shadow-sm mb-6" data-testid="tech-breakdown-card">
                      <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><Users size={20} />Technician Breakdown</h4>
                      {hasTechData ? (
                        <div className="overflow-x-auto">
                          <table className="w-full" data-testid="tech-breakdown-table">
                            <thead className="bg-slate-50 border-b border-slate-200">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Technician</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Rate/hr</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Hours</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">OT Hours</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">OT Cost</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Cost</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                              {Object.entries(costReport.technician_breakdown).map(([name, data]) => (
                                <tr key={name} className="hover:bg-slate-50">
                                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{name}</td>
                                  <td className="px-4 py-3 text-sm font-mono text-slate-600">${(data.rate || 0).toFixed(2)}</td>
                                  <td className="px-4 py-3 text-sm font-mono text-slate-900">{data.hours.toFixed(1)}</td>
                                  <td className={`px-4 py-3 text-sm font-mono ${(data.overtime_hours || 0) > 0 ? 'text-amber-700 font-semibold' : 'text-slate-400'}`}>
                                    {(data.overtime_hours || 0) > 0 ? data.overtime_hours.toFixed(1) : '-'}
                                  </td>
                                  <td className={`px-4 py-3 text-sm font-mono ${(data.overtime_cost || 0) > 0 ? 'text-amber-700 font-semibold' : 'text-slate-400'}`}>
                                    {(data.overtime_cost || 0) > 0 ? `$${data.overtime_cost.toFixed(2)}` : '-'}
                                  </td>
                                  <td className="px-4 py-3 text-sm font-mono font-bold text-slate-900">${data.cost.toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-slate-500 text-center py-4">No technician assignments with time data.</p>
                      )}
                    </Card>

                    {/* Equipment Breakdown */}
                    <Card className="p-6 border border-slate-200 shadow-sm" data-testid="equip-breakdown-card">
                      <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><Wrench size={20} />Equipment Breakdown</h4>
                      {hasEquipData ? (
                        <div className="overflow-x-auto">
                          <table className="w-full" data-testid="equip-breakdown-table">
                            <thead className="bg-slate-50 border-b border-slate-200">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Equipment</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Rate</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Hours</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Assignments</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Cost</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                              {Object.entries(costReport.equipment_breakdown).map(([name, data]) => (
                                <tr key={name} className="hover:bg-slate-50">
                                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{name}</td>
                                  <td className="px-4 py-3 text-sm font-mono text-slate-600">
                                    ${(data.rate || 0).toFixed(2)}{data.cost_type === 'per_use' ? '/use' : '/hr'}
                                  </td>
                                  <td className="px-4 py-3 text-sm font-mono text-slate-900">{data.hours.toFixed(1)}</td>
                                  <td className="px-4 py-3 text-sm font-mono text-slate-600">{data.assignments || '-'}</td>
                                  <td className="px-4 py-3 text-sm font-mono font-bold text-slate-900">${data.cost.toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-slate-500 text-center py-4">No equipment assignments with time data.</p>
                      )}
                    </Card>
                  </>
                )}
              </>
            ) : (
              <Card className="p-12 text-center border border-slate-200 shadow-sm">
                <p className="text-slate-600">No jobs available for reporting</p>
              </Card>
            )}
          </TabsContent>

          {/* Technician Margin Tab */}
          <TabsContent value="technician">
            <Card className="p-6 border border-slate-200 shadow-sm">
              <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Users size={20} />Average Margin by Technician
              </h4>
              <p className="text-sm text-slate-500 mb-4">Shows profitability of jobs each technician has worked on</p>
              {techMargins.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="tech-margin-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Technician</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Rate/hr</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Jobs</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Completed</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Revenue</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Cost</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Profit</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Avg Margin</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {techMargins.map((tech) => (
                        <tr key={tech.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">{tech.name}</td>
                          <td className="px-4 py-3 text-sm font-mono text-slate-600">${tech.hourly_rate.toFixed(2)}</td>
                          <td className="px-4 py-3 text-sm font-mono text-slate-600">{tech.total_jobs}</td>
                          <td className="px-4 py-3 text-sm font-mono text-slate-600">{tech.completed_jobs}</td>
                          <td className="px-4 py-3 text-sm font-mono text-slate-900">${tech.total_revenue.toFixed(2)}</td>
                          <td className="px-4 py-3 text-sm font-mono text-red-600">${tech.total_cost.toFixed(2)}</td>
                          <td className={`px-4 py-3 text-sm font-mono font-bold ${tech.total_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {tech.total_profit >= 0 ? '' : '-'}${Math.abs(tech.total_profit).toFixed(2)}
                          </td>
                          <td className="px-4 py-3">
                            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${tech.avg_margin >= 20 ? 'bg-emerald-100 text-emerald-700' : tech.avg_margin >= 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                              {tech.avg_margin >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                              {tech.avg_margin.toFixed(1)}%
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No technician data available. Assign technicians to jobs to see margin analysis.</p>
              )}
            </Card>
          </TabsContent>

          {/* Model Margin Tab */}
          <TabsContent value="model">
            <Card className="p-6 border border-slate-200 shadow-sm">
              <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Package size={20} />Average Margin by Make/Model
              </h4>
              <p className="text-sm text-slate-500 mb-4">Shows profitability of jobs by the make and model of equipment being repaired</p>
              {modelMargins.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="model-margin-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Make</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Model</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Jobs</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Completed</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Revenue</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Cost</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Profit</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Avg Margin</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {modelMargins.map((item) => (
                        <tr key={item.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">{item.make || '-'}</td>
                          <td className="px-4 py-3 text-sm text-slate-700">{item.model || '-'}</td>
                          <td className="px-4 py-3 text-sm font-mono text-slate-600">{item.total_jobs}</td>
                          <td className="px-4 py-3 text-sm font-mono text-slate-600">{item.completed_jobs}</td>
                          <td className="px-4 py-3 text-sm font-mono text-slate-900">${item.total_revenue.toFixed(2)}</td>
                          <td className="px-4 py-3 text-sm font-mono text-red-600">${item.total_cost.toFixed(2)}</td>
                          <td className={`px-4 py-3 text-sm font-mono font-bold ${item.total_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {item.total_profit >= 0 ? '' : '-'}${Math.abs(item.total_profit).toFixed(2)}
                          </td>
                          <td className="px-4 py-3">
                            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${item.avg_margin >= 20 ? 'bg-emerald-100 text-emerald-700' : item.avg_margin >= 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                              {item.avg_margin >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                              {item.avg_margin.toFixed(1)}%
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No model data available. Add make/model information to jobs to see margin analysis.</p>
              )}
            </Card>
          </TabsContent>

          {/* Customer Margin Tab */}
          <TabsContent value="customer">
            <Card className="p-6 border border-slate-200 shadow-sm">
              <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Building2 size={20} />Average Margin by Customer
              </h4>
              <p className="text-sm text-slate-500 mb-4">Shows profitability of jobs for each customer</p>
              {customerMargins.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="customer-margin-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Customer</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Total Jobs</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Completed</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Revenue</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Cost</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Profit</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Avg Margin</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {customerMargins.map((cust) => (
                        <tr key={cust.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">{cust.name}</td>
                          <td className="px-4 py-3 text-sm font-mono text-slate-600">{cust.total_jobs}</td>
                          <td className="px-4 py-3 text-sm font-mono text-slate-600">{cust.completed_jobs}</td>
                          <td className="px-4 py-3 text-sm font-mono text-slate-900">${cust.total_revenue.toFixed(2)}</td>
                          <td className="px-4 py-3 text-sm font-mono text-red-600">${cust.total_cost.toFixed(2)}</td>
                          <td className={`px-4 py-3 text-sm font-mono font-bold ${cust.total_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {cust.total_profit >= 0 ? '' : '-'}${Math.abs(cust.total_profit).toFixed(2)}
                          </td>
                          <td className="px-4 py-3">
                            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${cust.avg_margin >= 20 ? 'bg-emerald-100 text-emerald-700' : cust.avg_margin >= 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                              {cust.avg_margin >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                              {cust.avg_margin.toFixed(1)}%
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No customer data available. Create jobs with customers to see margin analysis.</p>
              )}
            </Card>
          </TabsContent>

          {/* Equipment Maintenance Tab */}
          <TabsContent value="maintenance">
            <Card className="p-6 border border-slate-200 shadow-sm" data-testid="maintenance-report-card">
              <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Wrench size={20} /> Equipment Maintenance Log
              </h4>
              <div className="flex flex-wrap items-end gap-3 mb-4 pb-4 border-b border-slate-200">
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold text-slate-500">Start date</label>
                  <DateField
                    className="mt-1"
                    value={maintRange.start_date}
                    onChange={(v) => setMaintRange({ ...maintRange, start_date: v })}
                    data-testid="maint-start-date"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold text-slate-500">End date</label>
                  <DateField
                    className="mt-1"
                    value={maintRange.end_date}
                    onChange={(v) => setMaintRange({ ...maintRange, end_date: v })}
                    data-testid="maint-end-date"
                  />
                </div>
                <button
                  onClick={() => fetchMaintenanceReport()}
                  className="bg-slate-900 text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-slate-800"
                  data-testid="maint-apply-btn"
                >
                  Apply
                </button>
                {(maintRange.start_date || maintRange.end_date) && (
                  <button
                    onClick={() => {
                      const cleared = { start_date: '', end_date: '' };
                      setMaintRange(cleared);
                      fetchMaintenanceReport(cleared);
                    }}
                    className="text-sm text-slate-600 underline"
                    data-testid="maint-clear-btn"
                  >
                    Clear
                  </button>
                )}
              </div>

              {maintenanceReport && maintenanceReport.equipment.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card className="p-4 border border-slate-200">
                      <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Total Hours</p>
                      <p className="text-2xl font-black text-slate-900 mt-1" data-testid="maint-total-hours">
                        {maintenanceReport.total_hours}
                        {maintenanceReport.total_overtime_hours > 0 && (
                          <span className="ml-2 text-sm font-semibold text-amber-700">
                            +{maintenanceReport.total_overtime_hours} OT
                          </span>
                        )}
                      </p>
                    </Card>
                    <Card className="p-4 border border-slate-200">
                      <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Total Cost</p>
                      <p className="text-2xl font-black text-slate-900 mt-1" data-testid="maint-total-cost">
                        ${maintenanceReport.total_cost.toFixed(2)}
                      </p>
                    </Card>
                    <Card className="p-4 border border-slate-200">
                      <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Equipment Count</p>
                      <p className="text-2xl font-black text-slate-900 mt-1">
                        {maintenanceReport.equipment.length}
                      </p>
                    </Card>
                  </div>

                  <div className="space-y-4" data-testid="maint-equipment-list">
                    {maintenanceReport.equipment.map((eq) => (
                      <Card key={eq.equipment_id} className="p-4 border border-slate-200" data-testid={`maint-eq-${eq.equipment_id}`}>
                        <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-100">
                          <div>
                            <p className="font-bold text-slate-900">{eq.equipment_name}</p>
                            <p className="text-xs text-slate-500">
                              {eq.entry_count} {eq.entry_count === 1 ? 'entry' : 'entries'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-slate-900">{eq.hours} hrs · ${eq.cost.toFixed(2)}</p>
                            {eq.overtime_hours > 0 && (
                              <p className="text-xs font-semibold text-amber-700">+{eq.overtime_hours} OT hrs</p>
                            )}
                          </div>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-xs uppercase tracking-wider text-slate-500">
                                <th className="text-left px-2 py-1">Date</th>
                                <th className="text-left px-2 py-1">Tech</th>
                                <th className="text-left px-2 py-1">Hours</th>
                                <th className="text-left px-2 py-1">OT</th>
                                <th className="text-left px-2 py-1">Cost</th>
                                <th className="text-left px-2 py-1">Description</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {eq.entries.map((e) => (
                                <tr key={e.id}>
                                  <td className="px-2 py-1 text-slate-900">{e.date}</td>
                                  <td className="px-2 py-1 text-slate-900">{e.tech_name}</td>
                                  <td className="px-2 py-1 font-mono text-slate-900">{e.hours}</td>
                                  <td className={`px-2 py-1 font-mono ${e.overtime_hours > 0 ? 'text-amber-700 font-semibold' : 'text-slate-400'}`}>
                                    {e.overtime_hours > 0 ? `+${e.overtime_hours}` : '-'}
                                  </td>
                                  <td className="px-2 py-1 font-mono text-slate-900">${e.cost.toFixed(2)}</td>
                                  <td className="px-2 py-1 text-slate-600">{e.description || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </Card>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-slate-500 text-center py-8">
                  No maintenance entries in this date range. Log shop time with the &ldquo;Equipment Maintenance&rdquo; category to see them here.
                </p>
              )}
            </Card>
          </TabsContent>

          {/* Compliance Report Tab */}
          <TabsContent value="compliance">
            <ComplianceReport />
          </TabsContent>
        </Tabs>
        )}
      </div>
    </Layout>
  );
}
